import { useMemo } from 'react';

import { Message, MessageType } from '@dreryk/react-components';

import { isQueryFailed, isQueryLoading } from '../../common/common.utils';
import { PageLoader } from '../../common/PageLoader';
import { nfzLoginTranslationKeys } from '../../translations';
import { MyAccountActionBar } from '../actionBar/MyAccountActionBar';

import { NfzLoginForm } from './form/NfzLoginForm';
import {
   useCurrentHealthcareProviderEwusConfigurationQuery,
   useCurrentPractitionerNfzConfigurationQuery,
   useCurrentProviderEzwmConfigurationQuery,
   useHealthcareProviderNfzConfigurationQuery,
} from './form/NfzLoginForm.hooks';
import { NfzLoginPanelNoConfiguration } from './NfzLoginPanelNoConfiguration';

export const NfzLoginPanel = (): JSX.Element => {
   const currentHealthcareProviderNfzConfigurationQuery = useHealthcareProviderNfzConfigurationQuery();
   const currentPractitionerNfzConfigurationQuery = useCurrentPractitionerNfzConfigurationQuery();
   const currentHealthcareProviderEwusConfigurationQuery = useCurrentHealthcareProviderEwusConfigurationQuery();
   const currentHealthcareProviderEzwmConfigurationQuery = useCurrentProviderEzwmConfigurationQuery();

   const errorLabel = useMemo(() => {
      if (isQueryFailed(currentPractitionerNfzConfigurationQuery, currentHealthcareProviderNfzConfigurationQuery)) {
         return nfzLoginTranslationKeys.failedToFetchNfzConfiguration;
      }
      if (isQueryFailed(currentHealthcareProviderEwusConfigurationQuery)) {
         return nfzLoginTranslationKeys.failedToFetchEwusConfiguration;
      }
      if (isQueryFailed(currentHealthcareProviderEzwmConfigurationQuery)) {
         return nfzLoginTranslationKeys.failedToFetchEzwmConfiguration;
      }
      return null;
   }, [
      currentHealthcareProviderNfzConfigurationQuery,
      currentPractitionerNfzConfigurationQuery,
      currentHealthcareProviderEzwmConfigurationQuery,
      currentHealthcareProviderEwusConfigurationQuery,
   ]);

   if (
      isQueryLoading(
         currentHealthcareProviderNfzConfigurationQuery,
         currentPractitionerNfzConfigurationQuery,
         currentHealthcareProviderEzwmConfigurationQuery,
         currentHealthcareProviderEwusConfigurationQuery
      )
   ) {
      return <PageLoader />;
   }

   if (errorLabel) {
      return (
         <Message
            testId={'failedToFetchIntegrationData'}
            label={errorLabel}
            type={MessageType.NEGATIVE}
            displayIndicator
         />
      );
   }

   const noSystemsIntegrationConfiguration =
      !currentHealthcareProviderEwusConfigurationQuery.data && !currentHealthcareProviderEzwmConfigurationQuery.data;

   if (!currentHealthcareProviderNfzConfigurationQuery.data || noSystemsIntegrationConfiguration) {
      return <NfzLoginPanelNoConfiguration />;
   }

   return (
      <MyAccountActionBar>
         <NfzLoginForm
            currentHealthcareProviderNfzConfiguration={currentHealthcareProviderNfzConfigurationQuery.data}
            currentPractitionerNfzConfiguration={currentPractitionerNfzConfigurationQuery.data ?? null}
            currentHealthcareProviderEwusEnabled={Boolean(currentHealthcareProviderEwusConfigurationQuery.data)}
            currentHealthcareProviderEzwmEnabled={Boolean(currentHealthcareProviderEzwmConfigurationQuery.data)}
         />
      </MyAccountActionBar>
   );
};
